import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { apiClient } from '../shared/api/client';
import { User } from '../shared/types';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  loginWithApple: () => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  verifyToken: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  console.log('🚀 AuthProvider component started!');
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  const isAuthenticated = !!user;

  console.log('🔧 AuthProvider render - isInitialized:', isInitialized, 'isLoading:', isLoading, 'user:', !!user);

  const initializeAuth = useCallback(async () => {
    try {
      console.log('🔄 Auth initialization started');
      // First handle OAuth callback if present
      const oauthHandled = await handleOAuthCallback();
      console.log('🔄 OAuth handled:', oauthHandled);
      
      // Always verify token if OAuth wasn't handled and we have a stored token
      if (!oauthHandled && !window.location.search.includes('token=') && !window.location.search.includes('code=')) {
        console.log('🔄 Calling verifyToken...');
        await verifyToken();
        console.log('🔄 verifyToken completed');
      } else {
        console.log('🔄 Skipping verifyToken, setting isLoading to false');
        // If OAuth was handled, just set loading to false
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Auth initialization error:', error);
      setUser(null);
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    console.log('🔄 useEffect triggered, isInitialized:', isInitialized);
    console.log('🔄 initializeAuth function:', typeof initializeAuth);
    if (!isInitialized) {
      console.log('🔄 Setting isInitialized to true and calling initializeAuth');
      setIsInitialized(true);
      initializeAuth();
    }
  }, [isInitialized, initializeAuth]);

  // Safety timeout to prevent loading state from getting stuck
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (isLoading) {
        console.log('Loading timeout reached, setting isLoading to false');
        setIsLoading(false);
      }
    }, 5000); // 5 second timeout

    return () => clearTimeout(timeout);
  }, [isLoading]);

  const handleOAuthCallback = async (): Promise<boolean> => {
    console.log('🔄 handleOAuthCallback started');
    
    // Check for OAuth success in URL hash (from server redirect)
    const hash = window.location.hash;
    console.log('🔄 Checking OAuth callback, hash:', hash);
    
    if (hash && hash.includes('apple_auth_success')) {
      console.log('🍎 Apple auth success detected');
      const urlParams = new URLSearchParams(hash.substring(1));
      const token = urlParams.get('token');
      const userParam = urlParams.get('user');
      
      console.log('🍎 Token:', token ? 'present' : 'missing');
      console.log('🍎 User param:', userParam ? 'present' : 'missing');
      
      if (token && userParam) {
        try {
          const user = JSON.parse(decodeURIComponent(userParam));
          localStorage.setItem('token', token);
          localStorage.setItem('user', JSON.stringify(user));
          setUser(user);
          setIsLoading(false);
          
          // Clean up URL without triggering reload
          window.history.replaceState({}, '', window.location.pathname + window.location.search);
          console.log('🍎 Apple login successful, user set:', user);
          return true;
        } catch (error) {
          console.error('🍎 Error parsing OAuth callback:', error);
        }
      }
    }
    
    // Check for OAuth callback in URL search params (from backend redirect)
    const searchParams = new URLSearchParams(window.location.search);
    const token = searchParams.get('token');
    const provider = searchParams.get('provider');
    const code = searchParams.get('code');
    const id_token = searchParams.get('id_token');
    const state = searchParams.get('state');
    
    // Apple callback is handled by server with form_post
    
    if (token && provider && window.location.pathname === '/auth/callback') {
      console.log('🔄 OAuth callback detected, provider:', provider, 'token:', token);
      
      // Verify token with backend
      try {
        const response = await apiClient.verifyToken();
        if (response.success && response.data) {
          localStorage.setItem('token', token);
          localStorage.setItem('user', JSON.stringify(response.data));
          setUser(response.data);
          setIsLoading(false);
          
          // Clean URL and redirect to home page without full reload
          window.history.replaceState({}, '', '/');
          console.log('🔄 OAuth login successful, user set:', response.data);
          return true;
        } else {
          console.error('🔄 Token verification failed');
          setUser(null);
          setIsLoading(false);
          window.history.replaceState({}, '', '/login?error=token_verification_failed');
          return true;
        }
      } catch (error) {
        console.error('🔄 Error verifying OAuth token:', error);
        setUser(null);
        setIsLoading(false);
        window.history.replaceState({}, '', '/login?error=oauth_failed');
        return true;
      }
    }
    
    // Also check URL search params for Google callback
    if (code && window.location.pathname === '/') {
      console.log('🔄 Google OAuth callback detected, code:', code);
      // Handle Google callback if needed
      return true;
    }
    
    console.log('🔄 No OAuth callback was handled');
    return false;
  };

  const verifyToken = async () => {
    try {
      console.log('🔍 verifyToken started');
      // First check if we have a stored user and token
      const storedUser = localStorage.getItem('user');
      const storedToken = localStorage.getItem('token');
      console.log('🔍 Stored user exists:', !!storedUser);
      console.log('🔍 Stored token exists:', !!storedToken);
      
      if (storedUser && storedToken) {
        const user = JSON.parse(storedUser);
        setUser(user);
        setIsLoading(false); // Set loading to false immediately after setting user
        
        // Verify with server in background but don't update state if it fails
        apiClient.verifyToken().then(response => {
          if (response.success && response.data) {
            // Only update if the user data is different
            const currentUser = JSON.stringify(user);
            const newUser = JSON.stringify(response.data);
            if (currentUser !== newUser) {
              setUser(response.data);
              localStorage.setItem('user', JSON.stringify(response.data));
            }
          }
        }).catch(error => {
          console.log('Background token verification failed:', error);
          // Don't clear user on background verification failure
        });
        return;
      }
      
      // No stored user, verify with server
      const response = await apiClient.verifyToken();
      if (response.success && response.data) {
        setUser(response.data);
        localStorage.setItem('user', JSON.stringify(response.data));
      } else {
        setUser(null);
        localStorage.removeItem('user');
        localStorage.removeItem('token');
      }
    } catch (error) {
      console.log('Token verification failed:', error);
      setUser(null);
      localStorage.removeItem('user');
      localStorage.removeItem('token');
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithApple = async () => {
    try {
      setIsLoading(true);
      
             // Generate state and nonce for security
             // eslint-disable-next-line @typescript-eslint/no-unused-vars
             const state = Math.random().toString(36).substring(2, 15);
      const nonce = Math.random().toString(36).substring(2, 15);
      
      // Store state and nonce for verification
      sessionStorage.setItem('apple_auth_state', state);
      sessionStorage.setItem('apple_auth_nonce', nonce);
      
      console.log('🍎 Redirecting to Apple Sign In...');
      console.log('🍎 State:', state);
      console.log('🍎 Nonce:', nonce);
      
      // Build Apple authorization URL (fixed according to documentation)
      const params = new URLSearchParams({
        client_id: 'se.budgetapp.web',
        redirect_uri: 'https://budgetapp.se/api/auth/apple/callback',
        response_type: 'code id_token',
        response_mode: 'form_post',
        scope: 'name email',
        state: state,
        nonce: nonce,
      });
      
      // Redirect to Apple
      window.location.href = `https://appleid.apple.com/auth/authorize?${params}`;
    } catch (error) {
      console.error('Apple login error:', error);
      setIsLoading(false);
    }
  };

  const loginWithGoogle = async () => {
    try {
      setIsLoading(true);
      
      // Use Google's OAuth flow
      const params = new URLSearchParams({
        client_id: '859753968620-i6n9fha5vj0hcp94c2lp1sujdqtm4t1p.apps.googleusercontent.com',
        redirect_uri: 'https://budgetapp.se/api/auth/google/callback',
        response_type: 'code',
        scope: 'openid email profile',
        access_type: 'offline',
      });
      
      // Redirect to Google
      window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
    } catch (error) {
      console.error('Google login error:', error);
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      setUser(null);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated,
    loginWithApple,
    loginWithGoogle,
    logout,
    verifyToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};